import {ApiProvider} from '@hconnect/common/hooks/useApi'
import {AuthProvider} from '@hconnect/login'
import {hpTheme} from '@hconnect/uikit'
import {UpmPlantsProvider} from '@hconnect/upm'
import {
  FundamentalContextProvider,
  ThemeProvider as FundamentalThemeProvider,
  ToastContainer
} from '@hm/fundamental'
import {CssBaseline, ThemeProvider} from '@mui/material'
import {DefaultOptions, QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {AxiosError, AxiosInstance} from 'axios'
import {HelmetProvider} from 'react-helmet-async'
import {useTranslation} from 'react-i18next'
import {BrowserRouter as Router} from 'react-router'

import {APP_NAME} from '../common/constants'
import {HelmetHead} from '../components/helmet/HelmetHead'
import {POMToursProvider} from '../providers/POMToursProvider'
import {FundamentalThemeInitializer} from '../shared/components'

interface GlobalProvidersProps {
  api: AxiosInstance
  children: React.ReactNode
}

const isNetworkError = (error: AxiosError) => error.message === 'Network Error'
const retryTries = 1
const retryFunction = (failureCount: number, error: Error): boolean => {
  return failureCount < retryTries && error instanceof AxiosError && isNetworkError(error)
}

const defaultOptions: DefaultOptions = {
  queries: {
    retry: retryFunction
  },
  mutations: {
    retry: retryFunction
  }
}

const queryClient = new QueryClient({
  defaultOptions: defaultOptions
})

export const GlobalProviders: React.FC<GlobalProvidersProps> = ({api, children}) => {
  const {t} = useTranslation()
  return (
    <HelmetProvider>
      <HelmetHead title={APP_NAME} description={t('appDescription')} />
      <FundamentalContextProvider>
        <FundamentalThemeProvider>
          <FundamentalThemeInitializer />
          <ThemeProvider theme={hpTheme}>
            <CssBaseline />
            <ToastContainer />
            <QueryClientProvider client={queryClient}>
              <ApiProvider secureApi={null} publicApi={api}>
                <POMToursProvider>
                  <Router>
                    <AuthProvider>
                      <UpmPlantsProvider api={api}>{children}</UpmPlantsProvider>
                    </AuthProvider>
                  </Router>
                </POMToursProvider>
              </ApiProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </FundamentalThemeProvider>
      </FundamentalContextProvider>
    </HelmetProvider>
  )
}
